import { Link } from "react-router-dom";
import { Globe, MapPin, Phone } from "lucide-react";
import type { GasStationBrand } from "@/types/database";
import { SafeImage } from "@/components/ui/SafeImage";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { formatNumber, imageSrc } from "@/lib/utils";

function BrandLogo({ brand }: { brand: GasStationBrand }) {
  const logoSrc = imageSrc(brand.brandLogoUrl);
  const name = brand.brandName ?? "Brand";

  if (logoSrc) {
    return (
      <SafeImage
        src={logoSrc}
        alt={name}
        width={48}
        height={48}
        className="h-12 w-12 object-contain"
      />
    );
  }

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent-subtle text-sm font-bold text-accent-foreground">
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

function BrandCard({ brand }: { brand: GasStationBrand }) {
  const name = brand.brandName ?? "Unnamed brand";
  const phone = brand.phone != null ? String(brand.phone) : null;

  return (
    <div className="rounded-2xl bg-canvas p-4 transition hover:bg-canvas-hover">
      <Link to={`/gas-station-brands/${brand.id}`} className="block">
        <div className="flex items-start gap-4">
          <BrandLogo brand={brand} />
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-bold text-foreground">{name}</h3>
            {brand.country ? (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{brand.country}</span>
              </p>
            ) : null}
          </div>
        </div>

        {brand.description ? (
          <p className="mt-3 line-clamp-2 text-sm text-muted">{brand.description}</p>
        ) : null}
      </Link>

      <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-muted">
        {brand.website ? (
          <a
            href={brand.website}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-full bg-background px-2.5 py-1 transition hover:text-foreground"
          >
            <Globe className="h-3 w-3" />
            Website
          </a>
        ) : null}
        {phone ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-background px-2.5 py-1">
            <Phone className="h-3 w-3" />
            {phone}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function GasStationBrandsSection({
  brands,
  isLoading,
  error,
}: {
  brands: GasStationBrand[];
  isLoading: boolean;
  error: Error | null;
}) {
  return (
    <Workspace>
      <WorkspaceSection className="p-4 sm:p-5">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-bold text-foreground">Gas station brands</h2>
            <p className="mt-1 text-sm text-muted">
              Catalogue of fuel brands available across the network.
            </p>
          </div>
          {!isLoading && !error ? (
            <p className="text-sm text-muted">
              <span className="font-bold text-foreground">{formatNumber(brands.length)}</span> brands
            </p>
          ) : null}
        </div>

        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-32 animate-pulse rounded-2xl bg-border/40" />
            ))}
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">Failed to load gas station brands.</p>
        ) : brands.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-canvas px-6 py-12 text-center">
            <p className="font-bold text-foreground">No brands yet</p>
            <p className="mt-1 max-w-sm text-sm text-muted">
              Add a gas station brand with the plus button above to build the catalogue.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {brands.map((brand) => (
              <BrandCard key={brand.id} brand={brand} />
            ))}
          </div>
        )}
      </WorkspaceSection>
    </Workspace>
  );
}
